/**
 * Rechnungen — iter139 Welle 3
 * Listet Rechnungen, Anlegen als Draft, Finalisieren (→ RE-YYYY-XXXX),
 * PDF-Preview, Als bezahlt markieren, Stornieren.
 */
import { useEffect, useState, useMemo } from "react";
import api, { API, getToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, FilePdf, CheckCircle, XCircle, Trash, PencilSimple, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_LABEL = { draft: "Entwurf", sent: "Versandt", paid: "Bezahlt", cancelled: "Storniert" };
const STATUS_COLOR = {
  draft: "bg-slate-100 text-slate-700",
  sent: "bg-blue-100 text-blue-800",
  paid: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-rose-100 text-rose-800",
};

const fmtEUR = (v) => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(Number(v || 0));
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("de-DE"); } catch { return iso; }
};

export default function Invoices() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (q) params.q = q;
      if (statusFilter !== "all") params.status = statusFilter;
      const { data } = await api.get("/invoices", { params });
      setItems(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, statusFilter]);

  const onSaved = () => { setOpen(false); setEditing(null); load(); };

  const previewPdf = async (id) => {
    try {
      const url = `${API}/invoices/${id}/pdf`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } });
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      window.open(objUrl, "_blank");
    } catch (e) { toast.error("PDF-Vorschau fehlgeschlagen"); }
  };

  const finalize = async (id) => {
    if (!await window._domivusConfirm("Rechnung finalisieren? Es wird eine fortlaufende Nummer vergeben (nicht mehr änderbar).")) return;
    try {
      const { data } = await api.post(`/invoices/${id}/finalize`);
      toast.success(`Rechnung ${data.invoice_number} erzeugt`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };

  const markPaid = async (id) => {
    try { await api.post(`/invoices/${id}/mark-paid`); toast.success("Als bezahlt markiert"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };
  const cancelInv = async (id) => {
    if (!await window._domivusConfirm("Rechnung stornieren?")) return;
    try { await api.post(`/invoices/${id}/cancel`); toast.success("Storniert"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };
  const del = async (id) => {
    if (!await window._domivusConfirm("Entwurf löschen?")) return;
    try { await api.delete(`/invoices/${id}`); toast.success("Gelöscht"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };

  return (
    <div className="space-y-4" data-testid="invoices-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Rechnungen</h1>
          <p className="text-sm text-slate-500 mt-1">Standard-Rechnungen (RE-YYYY-XXXX) außerhalb der BKA</p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800 text-white gap-2" data-testid="new-invoice-btn">
              <Plus size={16} /> Neue Rechnung
            </Button>
          </DialogTrigger>
          <InvoiceDialog key={open ? (editing?.id || "new") : "closed"} editing={editing} onSaved={onSaved} />
        </Dialog>
      </div>

      <div className="border-b border-slate-200 flex items-center gap-1 overflow-x-auto" data-testid="invoice-status-tabs">
        {[["all", "Alle"], ["draft", "Entwürfe"], ["sent", "Versandt"], ["paid", "Bezahlt"], ["cancelled", "Storniert"]].map(([k, v]) => (
          <button
            key={k}
            onClick={() => setStatusFilter(k)}
            data-testid={`invoice-tab-${k}`}
            className={`px-4 py-2 text-sm border-b-2 transition-colors whitespace-nowrap ${
              statusFilter === k ? "border-slate-900 text-slate-900 font-medium" : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >{v}</button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-sm p-3 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Suchen (Nummer, Empfänger, Notiz)…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 h-9" data-testid="invoice-search" />
        </div>
        <div className="text-xs text-slate-500 ml-auto">{items.length} Rechnung(en)</div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="min-w-full text-sm" data-testid="invoices-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-3 py-2">Nummer</th>
              <th className="px-3 py-2">Datum</th>
              <th className="px-3 py-2">Empfänger</th>
              <th className="px-3 py-2 text-right">Netto</th>
              <th className="px-3 py-2 text-right">Brutto</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2 text-right">Aktion</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-400">Lädt…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-400">Keine Rechnungen</td></tr>
            )}
            {items.map((inv) => (
              <tr key={inv.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`invoice-row-${inv.id}`}>
                <td className="px-3 py-2 font-mono text-xs">{inv.invoice_number || "—"}</td>
                <td className="px-3 py-2">{fmtDate(inv.invoice_date)}</td>
                <td className="px-3 py-2">{inv.recipient_snapshot?.name || "—"}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtEUR(inv.subtotal)}</td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold">{fmtEUR(inv.total)}</td>
                <td className="px-3 py-2">
                  <Badge className={STATUS_COLOR[inv.status] || ""}>{STATUS_LABEL[inv.status] || inv.status}</Badge>
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <div className="flex items-center gap-1 justify-end">
                    <button onClick={() => previewPdf(inv.id)} className="p-1.5 hover:bg-slate-200 rounded" title="PDF-Vorschau" data-testid={`invoice-pdf-${inv.id}`}><FilePdf size={16} /></button>
                    {inv.status === "draft" && (
                      <>
                        <button onClick={() => { setEditing(inv); setOpen(true); }} className="p-1.5 hover:bg-slate-200 rounded" title="Bearbeiten" data-testid={`invoice-edit-${inv.id}`}><PencilSimple size={16} /></button>
                        <button onClick={() => finalize(inv.id)} className="p-1.5 hover:bg-emerald-100 rounded text-emerald-700" title="Finalisieren" data-testid={`invoice-finalize-${inv.id}`}><CheckCircle size={16} /></button>
                        <button onClick={() => del(inv.id)} className="p-1.5 hover:bg-rose-100 rounded text-rose-600" title="Löschen" data-testid={`invoice-delete-${inv.id}`}><Trash size={16} /></button>
                      </>
                    )}
                    {inv.status === "sent" && (
                      <>
                        <button onClick={() => markPaid(inv.id)} className="p-1.5 hover:bg-emerald-100 rounded text-emerald-700" title="Als bezahlt markieren" data-testid={`invoice-paid-${inv.id}`}><CheckCircle size={16} /></button>
                        <button onClick={() => cancelInv(inv.id)} className="p-1.5 hover:bg-rose-100 rounded text-rose-600" title="Stornieren" data-testid={`invoice-cancel-${inv.id}`}><XCircle size={16} /></button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- Dialog */
function InvoiceDialog({ editing, onSaved }) {
  const [recipient, setRecipient] = useState(editing?.recipient_contact_id || "");
  const [contacts, setContacts] = useState([]);
  const [contactQ, setContactQ] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(editing?.invoice_date || new Date().toISOString().slice(0, 10));
  const [dueDays, setDueDays] = useState(editing?.due_days || 14);
  const [notes, setNotes] = useState(editing?.notes || "");
  const [items, setItems] = useState(
    (editing?.line_items && editing.line_items.length)
      ? editing.line_items
      : [{ description: "", quantity: 1, unit_price: 0, vat_pct: 19, unit: "" }]
  );

  useEffect(() => {
    api.get("/contacts", { params: { limit: 500 } }).then(({ data }) => setContacts(data)).catch(() => setContacts([]));
  }, []);

  const filteredContacts = useMemo(() => {
    const q = contactQ.trim().toLowerCase();
    if (!q) return contacts.slice(0, 50);
    return contacts.filter((c) => {
      const name = `${c.first_name || ""} ${c.last_name || ""} ${c.company_name || ""}`.toLowerCase();
      return name.includes(q);
    }).slice(0, 50);
  }, [contacts, contactQ]);

  const totals = useMemo(() => {
    let net = 0, vat = 0;
    for (const it of items) {
      const l = Number(it.quantity || 0) * Number(it.unit_price || 0);
      net += l;
      vat += l * Number(it.vat_pct || 0) / 100;
    }
    return { net, vat, gross: net + vat };
  }, [items]);

  const updateItem = (idx, patch) => setItems((cur) => cur.map((it, i) => i === idx ? { ...it, ...patch } : it));
  const addItem = () => setItems((cur) => [...cur, { description: "", quantity: 1, unit_price: 0, vat_pct: 19, unit: "" }]);
  const removeItem = (idx) => setItems((cur) => cur.filter((_, i) => i !== idx));

  const save = async () => {
    if (!recipient) { toast.error("Bitte Empfänger wählen"); return; }
    if (!items.length) { toast.error("Mindestens eine Position erforderlich"); return; }
    const payload = {
      recipient_contact_id: recipient,
      invoice_date: invoiceDate,
      due_days: Number(dueDays),
      line_items: items.map((it) => ({
        description: it.description || "",
        quantity: Number(it.quantity || 0),
        unit_price: Number(it.unit_price || 0),
        vat_pct: Number(it.vat_pct || 0),
        unit: it.unit || null,
      })),
      notes: notes || null,
    };
    try {
      if (editing?.id) {
        await api.patch(`/invoices/${editing.id}`, payload);
        toast.success("Gespeichert");
      } else {
        await api.post(`/invoices`, payload);
        toast.success("Entwurf erstellt");
      }
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler beim Speichern"); }
  };

  return (
    <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="invoice-dialog">
      <DialogHeader>
        <DialogTitle>{editing ? "Rechnung bearbeiten" : "Neue Rechnung"}</DialogTitle>
      </DialogHeader>
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <Label>Empfänger *</Label>
            <Input value={contactQ} onChange={(e) => setContactQ(e.target.value)} placeholder="Kontakt suchen…" className="mb-1" data-testid="invoice-contact-search" />
            <select value={recipient} onChange={(e) => setRecipient(e.target.value)} className="w-full h-9 border border-slate-200 rounded px-2 text-sm" data-testid="invoice-recipient">
              <option value="">— wählen —</option>
              {filteredContacts.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.first_name ? `${c.first_name} ${c.last_name || ""}` : (c.company_name || "(unbenannt)")}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label>Rechnungsdatum</Label>
            <Input type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} data-testid="invoice-date" />
            <div className="mt-2">
              <Label>Fällig in (Tagen)</Label>
              <Input type="number" min="0" value={dueDays} onChange={(e) => setDueDays(e.target.value)} data-testid="invoice-due-days" />
            </div>
          </div>
        </div>

        <div className="border border-slate-200 rounded-md">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-2 py-1.5 text-left">Beschreibung</th>
                <th className="px-2 py-1.5 text-right w-24">Menge</th>
                <th className="px-2 py-1.5 text-left w-20">Einheit</th>
                <th className="px-2 py-1.5 text-right w-28">Einzelpreis</th>
                <th className="px-2 py-1.5 text-right w-20">USt %</th>
                <th className="px-2 py-1.5 text-right w-28">Netto</th>
                <th className="px-2 py-1.5 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => (
                <tr key={idx} className="border-t border-slate-100" data-testid={`invoice-line-${idx}`}>
                  <td className="px-1"><Input value={it.description} onChange={(e) => updateItem(idx, { description: e.target.value })} className="h-8" data-testid={`invoice-line-desc-${idx}`} /></td>
                  <td className="px-1"><Input type="number" step="0.01" value={it.quantity} onChange={(e) => updateItem(idx, { quantity: e.target.value })} className="h-8 text-right" data-testid={`invoice-line-qty-${idx}`} /></td>
                  <td className="px-1"><Input value={it.unit || ""} onChange={(e) => updateItem(idx, { unit: e.target.value })} placeholder="Std." className="h-8" /></td>
                  <td className="px-1"><Input type="number" step="0.01" value={it.unit_price} onChange={(e) => updateItem(idx, { unit_price: e.target.value })} className="h-8 text-right" data-testid={`invoice-line-price-${idx}`} /></td>
                  <td className="px-1">
                    <select value={it.vat_pct} onChange={(e) => updateItem(idx, { vat_pct: e.target.value })} className="h-8 w-full border border-slate-200 rounded text-sm" data-testid={`invoice-line-vat-${idx}`}>
                      <option value={0}>0 %</option>
                      <option value={7}>7 %</option>
                      <option value={19}>19 %</option>
                    </select>
                  </td>
                  <td className="px-2 text-right tabular-nums">{fmtEUR(Number(it.quantity || 0) * Number(it.unit_price || 0))}</td>
                  <td className="px-1 text-center">
                    <button onClick={() => removeItem(idx)} className="text-rose-600 hover:bg-rose-50 rounded p-1" title="Zeile entfernen"><Trash size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-2 py-2 flex items-center justify-between bg-slate-50 border-t border-slate-200">
            <Button variant="outline" size="sm" onClick={addItem} className="gap-1" data-testid="invoice-add-line">
              <Plus size={14} /> Position
            </Button>
            <div className="text-sm text-right">
              <div>Netto: <span className="font-semibold tabular-nums">{fmtEUR(totals.net)}</span></div>
              <div>USt: <span className="font-semibold tabular-nums">{fmtEUR(totals.vat)}</span></div>
              <div className="text-base">Gesamt: <span className="font-bold tabular-nums">{fmtEUR(totals.gross)}</span></div>
            </div>
          </div>
        </div>

        <div>
          <Label>Notizen (optional)</Label>
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="invoice-notes" />
        </div>
      </div>
      <DialogFooter>
        <Button onClick={save} className="bg-slate-900 hover:bg-slate-800" data-testid="invoice-save">Speichern</Button>
      </DialogFooter>
    </DialogContent>
  );
}
